import { GoogleGenAI, Type } from "@google/genai";
import Tesseract from "tesseract.js";
import { Transaction, Category } from "../types";

export interface FinancialInsight {
  id: string;
  title: string;
  advice: string;
  type: 'neutral' | 'positive' | 'warning';
}

// Fixed: Migrated from Groq to Google Gemini API using @google/genai.
export const getFinancialAudit = async (transactions: Transaction[], userContext?: string): Promise<FinancialInsight[]> => {
  if (transactions.length === 0) return [];

  // Use the API key directly from the environment.
  if (!process.env.API_KEY) return [];

  const summary = transactions.map(t => ({
    type: t.type,
    amount: t.amount,
    cat: t.category,
    date: t.date,
    note: t.note
  }));

  // Initializing with process.env.API_KEY directly.
  const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
  try {
    const response = await ai.models.generateContent({
      model: "gemini-3-flash-preview",
      contents: `Analyze these transactions. ${userContext ? `Focus on this area: ${userContext}.` : ""} 
            Data: ${JSON.stringify(summary)}`,
      config: {
        systemInstruction: "You are a professional financial advisor. Analyze transaction data and provide 3 helpful insights/tips.",
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            insights: {
              type: Type.ARRAY,
              items: {
                type: Type.OBJECT,
                properties: {
                  id: { type: Type.STRING },
                  title: { type: Type.STRING },
                  advice: { type: Type.STRING },
                  type: { type: Type.STRING, enum: ['neutral', 'positive', 'warning'] }
                },
                required: ['id', 'title', 'advice', 'type']
              }
            }
          },
          required: ['insights']
        }
      }
    });

    // Using .text property instead of .text() method.
    const result = JSON.parse(response.text || '{}');
    return result.insights || [];
  } catch (e) {
    console.error("AI Audit Error:", e);
    return [];
  }
};

export interface ScannedReceipt {
  amount: number;
  date: string;
  category: Category;
  note: string;
}

const mapToValidCategory = (aiCat: string): Category => {
  const cat = (aiCat || '').trim().toLowerCase();
  if (cat.includes('bill')) return Category.BILLS;
  if (cat.includes('food') || cat.includes('eat')) return Category.FOOD;
  if (cat.includes('travel') || cat.includes('uber')) return Category.TRAVEL;
  if (cat.includes('shop')) return Category.SHOPPING;
  if (cat.includes('health')) return Category.HEALTH;
  if (cat.includes('edu')) return Category.EDUCATION;
  if (cat.includes('ent')) return Category.ENTERTAINMENT;
  if (cat.includes('salary')) return Category.SALARY;
  if (cat.includes('invest')) return Category.INVESTMENT;
  return Category.OTHERS;
};

// Updated: Uses Tesseract OCR and Grok API for receipt parsing.
const extractReceiptFromText = async (text: string, apiKey: string): Promise<ScannedReceipt | null> => {
  if (!text.trim()) return null;

  const response = await fetch('https://api.groq.com/openai/v1/chat/completions', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${apiKey}`
    },
    body: JSON.stringify({
      model: 'llama-3.3-70b-versatile',
      messages: [
        {
          role: 'system',
          content: `You are a highly accurate financial auditor AI. Extract data from this OCR text of a Receipt, Invoice, or Bill.
The text might be messy and unstructured. Please handle different types of invoices carefully:
1. Supermarket/Retail (e.g., D-Mart, supermarkets): Look for "Grand Total", "Net Amount", or "Total Amount". Set category to 'Shopping' or 'Food'.
2. Restaurant/Cafe: Look for the final total including all taxes (CGST/SGST/Service Charge). Set category to 'Food'.
3. Utility Bills (Electricity, Water, Gas): Look for "Total Amount Due", "Net Payable". Set category to 'Bills'.
4. Other Invoices: Find the final paid amount.

Output ONLY a valid RAW JSON object without any markdown formatting or code blocks.
Fields required:
{
  "amount": number (The FINAL TOTAL PAID amount as a plain number, e.g. 1500.50),
  "date": string (Purchase/Bill date in YYYY-MM-DD. Guess if necessary),
  "category": string (Must be strictly one of ['Food', 'Travel', 'Shopping', 'Bills', 'Health', 'Education', 'Entertainment', 'Others']),
  "note": string (The name of the store, restaurant, or utility provider)
}`
        },
        {
          role: 'user',
          content: `Here is the OCR text extracted from the receipt: """${text}"""`
        }
      ],
      temperature: 0.1
    })
  });

  if (!response.ok) {
    console.error("Groq API Error:", response.status, await response.text());
    return null;
  }

  const jsonResponse = await response.json();
  let content = jsonResponse.choices?.[0]?.message?.content || '{}';
  content = content.replace(/```json/g, '').replace(/```/g, '').trim();

  const result = JSON.parse(content);
  if (!result.amount) return null;

  return {
    amount: Number(result.amount) || 0,
    date: result.date || new Date().toISOString().split('T')[0],
    category: mapToValidCategory(result.category),
    note: result.note || 'Scanned Bill'
  };
};

export const scanReceipt = async (base64Image: string | string[]): Promise<ScannedReceipt | null> => {
  // @ts-ignore
  const apiKey = import.meta.env.VITE_GROK_API_KEY || import.meta.env.VITE_API_KEY;
  if (!apiKey) {
    console.error("Missing VITE_GROK_API_KEY");
    return null;
  }

  try {
    const images = Array.isArray(base64Image) ? base64Image : [base64Image];

    for (const image of images) {
      console.log("Running Tesseract OCR...");
      const { data: { text } } = await Tesseract.recognize(image, 'eng');
      console.log("Extracted OCR Text:", text);

      const result = await extractReceiptFromText(text, apiKey);
      if (result) {
        return result;
      }
    }

    return null;
  } catch (e) {
    console.error("AI Scan Error:", e);
    return null;
  }
};
